import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import {
  Search,
  Lock,
  Target,
  Database,
  Wrench,
  AlertTriangle,
  Zap,
  Clock,
  Shield,
  Eye,
  Info,
  ChevronDown,
  ChevronUp,
  Activity,
  Users,
  Server,
  Wifi,
  HardDrive,
  Key,
  FileText,
  Globe
} from 'lucide-react';

/**
 * Enhanced React Flow node components for EBIOS RM phase visualization
 * Each node type has specific styling, icons, and interactive elements
 */

// Main Phase Node - Central node representing an EBIOS RM phase
export const PhaseNode = ({ data, selected }) => {
  const [expanded, setExpanded] = useState(false);

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'CONNAITRE': return <Search className="h-5 w-5" />;
      case 'RENTRER': return <Lock className="h-5 w-5" />;
      case 'TROUVER': return <Target className="h-5 w-5" />;
      case 'EXPLOITER': return <Database className="h-5 w-5" />;
      default: return <Eye className="h-5 w-5" />;
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'CONNAITRE': return 'bg-blue-500 border-blue-600 shadow-blue-200'; // Blue - same as old
      case 'RENTRER': return 'bg-red-500 border-red-600 shadow-red-200'; // Red - same as old
      case 'TROUVER': return 'bg-amber-500 border-amber-600 shadow-amber-200'; // Amber - same as old
      case 'EXPLOITER': return 'bg-green-500 border-green-600 shadow-green-200'; // Green - same as old
      default: return 'bg-gray-500 border-gray-600 shadow-gray-200';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Très faible': return 'bg-green-100 text-green-800';
      case 'Faible': return 'bg-green-100 text-green-800';
      case 'Moyen': return 'bg-yellow-100 text-yellow-800';
      case 'Élevé': return 'bg-orange-100 text-orange-800';
      case 'Très élevé': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const phaseColor = data.phase === 'CONNAITRE' ? '#3B82F6' :
    data.phase === 'RENTRER' ? '#EF4444' :
      data.phase === 'TROUVER' ? '#F59E0B' :
        data.phase === 'EXPLOITER' ? '#10B981' : '#6B7280';

  const phaseIcon = data.phase === 'CONNAITRE' ? '1' :
    data.phase === 'RENTRER' ? '2' :
      data.phase === 'TROUVER' ? '3' :
        data.phase === 'EXPLOITER' ? '4' : '•';

  return (
    <div
      className={`relative border-2 rounded-xl shadow-lg w-96 bg-white transition-all duration-200 hover:shadow-xl ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'
        }`}
      style={{ borderColor: selected ? '#3B82F6' : phaseColor }}
    >
      {/* Input Handle */}
      <Handle type="target" position={Position.Left} style={{ background: phaseColor, width: 12, height: 12 }} />

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} style={{ background: phaseColor, width: 12, height: 12 }} />
      {/* Phase header with icon and name */}
      <div
        className="flex items-center text-white font-bold text-sm py-3 px-4 rounded-t-xl"
        style={{ backgroundColor: phaseColor }}
      >
        <div
          className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3 text-sm font-bold shadow-sm"
          style={{ color: phaseColor }}
        >
          {phaseIcon}
        </div>
        <div className="flex-1">
          <div className="font-bold text-base">{data.phase || 'PHASE'}</div>
          <div className="text-xs opacity-90 font-normal">{data.name}</div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Description */}
        <div className="mb-4">
          <p className="text-gray-700 text-sm leading-relaxed font-medium">{data.description}</p>
        </div>


        {/* Techniques Section */}
        {data.techniques && data.techniques.length > 0 && (
          <div className="mb-4">
            <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center">
              <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: phaseColor }}></div>
              Techniques:
            </h5>
            <div className="space-y-2">
              {data.techniques.slice(0, 4).map((technique, index) => (
                <div key={index} className="bg-purple-50 border border-purple-200 px-3 py-2 rounded-lg text-xs">
                  <div className="font-medium text-purple-800">
                    {technique.name || technique.technique || technique}
                  </div>
                  {technique.description && (
                    <div className="text-purple-600 mt-1 text-xs">
                      {technique.description}
                    </div>
                  )}
                </div>
              ))}
              {data.techniques.length > 4 && (
                <div className="text-gray-500 italic text-xs text-center py-1">
                  +{data.techniques.length - 4} techniques supplémentaires
                </div>
              )}
            </div>
          </div>
        )}

        {/* Indicators Section */}
        {data.indicators && data.indicators.length > 0 && (
          <div className="mb-4">
            <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center">
              <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: phaseColor }}></div>
              Indicateurs:
            </h5>
            <div className="space-y-2">
              {data.indicators.slice(0, 4).map((indicator, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-lg text-xs">
                  <div className="font-medium text-yellow-800">
                    {indicator.name || indicator.indicator || indicator}
                  </div>
                  {indicator.description && (
                    <div className="text-yellow-600 mt-1 text-xs">
                      {indicator.description}
                    </div>
                  )}
                </div>
              ))}
              {data.indicators.length > 4 && (
                <div className="text-gray-500 italic text-xs text-center py-1">
                  +{data.indicators.length - 4} indicateurs supplémentaires
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tools Section */}
        {data.tools && data.tools.length > 0 && (
          <div className="mb-2">
            <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center">
              <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: phaseColor }}></div>
              Outils:
            </h5>
            <div className="flex flex-wrap gap-1">
              {data.tools.slice(0, 6).map((tool, index) => (
                <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium border border-green-200">
                  {tool.name || tool}
                </span>
              ))}
              {data.tools.length > 6 && (
                <span className="text-gray-500 italic text-xs px-2 py-1">
                  +{data.tools.length - 6} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Technique Node - Represents attack techniques
export const TechniqueNode = ({ data, selected }) => {
  const getTechniqueIcon = (technique) => {
    if (technique?.toLowerCase().includes('social')) return <Users className="h-4 w-4" />;
    if (technique?.toLowerCase().includes('network')) return <Wifi className="h-4 w-4" />;
    if (technique?.toLowerCase().includes('system')) return <Server className="h-4 w-4" />;
    if (technique?.toLowerCase().includes('file')) return <FileText className="h-4 w-4" />;
    if (technique?.toLowerCase().includes('web')) return <Globe className="h-4 w-4" />;
    return <Wrench className="h-4 w-4" />;
  };

  return (
    <div className={`
      px-3 py-2 shadow-md rounded-md border border-gray-300 bg-white min-w-[150px] max-w-[200px]
      ${selected ? 'ring-2 ring-blue-500' : ''}
      hover:shadow-lg transition-all duration-200
    `}>
      {/* Input Handle */}
      <Handle type="target" position={Position.Left} style={{ background: '#3b82f6' }} />

      <div className="flex items-center space-x-2 mb-1">
        <div className="text-gray-600">
          {getTechniqueIcon(data.label)}
        </div>
        <div className="font-semibold text-sm text-gray-900 flex-1">{data.label}</div>
      </div>
      {data.description && (
        <div className="text-xs text-gray-600 line-clamp-2">{data.description}</div>
      )}
      {data.mitreId && (
        <div className="mt-1 text-xs text-blue-600 font-mono">{data.mitreId}</div>
      )}

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} style={{ background: '#3b82f6' }} />
    </div>
  );
};

// Indicator Node - Represents detection indicators
export const IndicatorNode = ({ data, selected }) => {
  const getIndicatorIcon = (indicator) => {
    if (indicator?.toLowerCase().includes('log')) return <FileText className="h-4 w-4" />;
    if (indicator?.toLowerCase().includes('network')) return <Wifi className="h-4 w-4" />;
    if (indicator?.toLowerCase().includes('process')) return <Activity className="h-4 w-4" />;
    if (indicator?.toLowerCase().includes('file')) return <HardDrive className="h-4 w-4" />;
    return <AlertTriangle className="h-4 w-4" />;
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'border-red-400 bg-red-50';
      case 'high': return 'border-orange-400 bg-orange-50';
      case 'medium': return 'border-yellow-400 bg-yellow-50';
      case 'low': return 'border-green-400 bg-green-50';
      default: return 'border-yellow-300 bg-yellow-50';
    }
  };

  return (
    <div className={`
      px-3 py-2 shadow-md rounded-md border-2 min-w-[120px] max-w-[180px]
      ${getSeverityColor(data.severity)}
      ${selected ? 'ring-2 ring-yellow-500' : ''}
      hover:shadow-lg transition-all duration-200
    `}>
      {/* Input Handle */}
      <Handle type="target" position={Position.Left} style={{ background: '#f59e0b' }} />

      <div className="flex items-center space-x-2 mb-1">
        <div className="text-yellow-600">
          {getIndicatorIcon(data.label)}
        </div>
        <div className="font-semibold text-sm text-yellow-900">Indicateur</div>
      </div>
      <div className="text-xs text-yellow-800 font-medium">{data.label}</div>
      {data.description && (
        <div className="text-xs text-yellow-700 mt-1 line-clamp-2">{data.description}</div>
      )}
      {data.severity && (
        <div className="mt-1">
          <span className={`px-1 py-0.5 rounded text-xs font-medium ${data.severity === 'Critical' ? 'bg-red-100 text-red-800' :
              data.severity === 'High' ? 'bg-orange-100 text-orange-800' :
                data.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
            }`}>
            {data.severity}
          </span>
        </div>
      )}

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} style={{ background: '#f59e0b' }} />
    </div>
  );
};

// Tool Node - Represents attack tools
export const ToolNode = ({ data, selected }) => {
  const getToolIcon = (tool) => {
    if (tool?.toLowerCase().includes('nmap')) return <Wifi className="h-4 w-4" />;
    if (tool?.toLowerCase().includes('metasploit')) return <Zap className="h-4 w-4" />;
    if (tool?.toLowerCase().includes('powershell')) return <Server className="h-4 w-4" />;
    if (tool?.toLowerCase().includes('mimikatz')) return <Key className="h-4 w-4" />;
    if (tool?.toLowerCase().includes('burp')) return <Globe className="h-4 w-4" />;
    return <Wrench className="h-4 w-4" />;
  };

  const getToolCategory = (tool) => {
    if (tool?.toLowerCase().includes('scan')) return 'Scanner';
    if (tool?.toLowerCase().includes('exploit')) return 'Exploit';
    if (tool?.toLowerCase().includes('credential')) return 'Credential';
    if (tool?.toLowerCase().includes('lateral')) return 'Lateral Movement';
    return 'Tool';
  };

  return (
    <div className={`
      px-3 py-2 shadow-md rounded-md border border-green-300 bg-green-50 min-w-[120px] max-w-[180px]
      ${selected ? 'ring-2 ring-green-500' : ''}
      hover:shadow-lg transition-all duration-200
    `}>
      {/* Input Handle */}
      <Handle type="target" position={Position.Left} style={{ background: '#10b981' }} />

      <div className="flex items-center space-x-2 mb-1">
        <div className="text-green-600">
          {getToolIcon(data.label)}
        </div>
        <div className="font-semibold text-sm text-green-900">Outil</div>
      </div>
      <div className="text-xs text-green-800 font-medium">{data.label}</div>
      {data.description && (
        <div className="text-xs text-green-700 mt-1 line-clamp-2">{data.description}</div>
      )}
      <div className="mt-1">
        <span className="px-1 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
          {getToolCategory(data.label)}
        </span>
      </div>

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} style={{ background: '#10b981' }} />
    </div>
  );
};

// Asset Node - Represents business or support assets
export const AssetNode = ({ data, selected }) => {
  const getAssetIcon = (assetType) => {
    switch (assetType?.toLowerCase()) {
      case 'server': return <Server className="h-4 w-4" />;
      case 'database': return <Database className="h-4 w-4" />;
      case 'network': return <Wifi className="h-4 w-4" />;
      case 'application': return <Globe className="h-4 w-4" />;
      case 'file': return <FileText className="h-4 w-4" />;
      default: return <Shield className="h-4 w-4" />;
    }
  };

  const getAssetColor = (criticality) => {
    switch (criticality?.toLowerCase()) {
      case 'critical': return 'border-red-300 bg-red-50';
      case 'high': return 'border-orange-300 bg-orange-50';
      case 'medium': return 'border-blue-300 bg-blue-50';
      case 'low': return 'border-gray-300 bg-gray-50';
      default: return 'border-blue-300 bg-blue-50';
    }
  };

  return (
    <div className={`
      px-3 py-2 shadow-md rounded-md border-2 min-w-[140px] max-w-[200px]
      ${getAssetColor(data.criticality)}
      ${selected ? 'ring-2 ring-blue-500' : ''}
      hover:shadow-lg transition-all duration-200
    `}>
      {/* Input Handle */}
      <Handle type="target" position={Position.Left} style={{ background: '#3b82f6' }} />

      <div className="flex items-center space-x-2 mb-1">
        <div className="text-blue-600">
          {getAssetIcon(data.assetType)}
        </div>
        <div className="font-semibold text-sm text-blue-900">Actif</div>
      </div>
      <div className="text-xs text-blue-800 font-medium">{data.label}</div>
      {data.description && (
        <div className="text-xs text-blue-700 mt-1 line-clamp-2">{data.description}</div>
      )}
      {data.criticality && (
        <div className="mt-1">
          <span className={`px-1 py-0.5 rounded text-xs font-medium ${data.criticality === 'Critical' ? 'bg-red-100 text-red-800' :
              data.criticality === 'High' ? 'bg-orange-100 text-orange-800' :
                data.criticality === 'Medium' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
            }`}>
            {data.criticality}
          </span>
        </div>
      )}

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} style={{ background: '#3b82f6' }} />
    </div>
  );
};

// Threat Source Node - Represents the attacker/threat source
export const ThreatSourceNode = ({ data, selected }) => {
  const getThreatIcon = (threatType) => {
    switch (threatType?.toLowerCase()) {
      case 'cybercrime': return <Users className="h-4 w-4" />;
      case 'apt': return <Shield className="h-4 w-4" />;
      case 'insider': return <Key className="h-4 w-4" />;
      case 'hacktivist': return <Globe className="h-4 w-4" />;
      case 'script-kiddie': return <Activity className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const getThreatColor = (threatType) => {
    switch (threatType?.toLowerCase()) {
      case 'cybercrime': return 'border-red-400 bg-red-50';
      case 'apt': return 'border-purple-400 bg-purple-50';
      case 'insider': return 'border-orange-400 bg-orange-50';
      case 'hacktivist': return 'border-blue-400 bg-blue-50';
      case 'script-kiddie': return 'border-green-400 bg-green-50';
      default: return 'border-gray-400 bg-gray-50';
    }
  };

  return (
    <div className={`
      px-4 py-3 shadow-md rounded-md border-2 min-w-[180px] max-w-[240px]
      ${getThreatColor(data.threatType)}
      ${selected ? 'ring-2 ring-red-500' : ''}
      hover:shadow-lg transition-all duration-200
    `}>
      {/* Output Handle Only - Threat sources initiate attacks */}
      <Handle type="source" position={Position.Right} style={{ background: '#dc2626' }} />

      <div className="flex items-center space-x-2 mb-2">
        <div className="text-red-600">
          {getThreatIcon(data.threatType)}
        </div>
        <div className="font-semibold text-sm text-red-900">Source de Menace</div>
      </div>
      <div className="text-xs text-red-800 font-medium mb-1">{data.label}</div>
      {data.description && (
        <div className="text-xs text-red-700 mt-1 line-clamp-2">{data.description}</div>
      )}
      {data.capability && (
        <div className="mt-2">
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
            {data.capability}
          </span>
        </div>
      )}
      {data.motivation && (
        <div className="mt-1">
          <span className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs">
            {data.motivation}
          </span>
        </div>
      )}
    </div>
  );
};

// Vulnerability Node - Represents CVE vulnerabilities
export const VulnerabilityNode = ({ data, selected }) => {
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'border-red-400 bg-red-50 text-red-800';
      case 'high': return 'border-orange-400 bg-orange-50 text-orange-800';
      case 'medium': return 'border-yellow-400 bg-yellow-50 text-yellow-800';
      case 'low': return 'border-green-400 bg-green-50 text-green-800';
      default: return 'border-gray-400 bg-gray-50 text-gray-800';
    }
  };

  return (
    <div className={`
      px-3 py-2 shadow-md rounded-md border-2 min-w-[160px] max-w-[220px]
      ${getSeverityColor(data.severity)}
      ${selected ? 'ring-2 ring-red-500' : ''}
      hover:shadow-lg transition-all duration-200
    `}>
      {/* Input Handle */}
      <Handle type="target" position={Position.Left} style={{ background: '#ef4444' }} />

      <div className="flex items-center space-x-2 mb-1">
        <AlertTriangle className="h-4 w-4" />
        <div className="font-semibold text-sm">Vulnérabilité</div>
      </div>
      <div className="text-xs font-medium">{data.cveId || data.label}</div>
      {data.description && (
        <div className="text-xs mt-1 line-clamp-2">{data.description}</div>
      )}
      <div className="flex items-center justify-between mt-2">
        {data.severity && (
          <span className={`px-1 py-0.5 rounded text-xs font-medium ${data.severity === 'Critical' ? 'bg-red-100 text-red-800' :
              data.severity === 'High' ? 'bg-orange-100 text-orange-800' :
                data.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
            }`}>
            {data.severity}
          </span>
        )}
        {data.cvssScore && (
          <span className="text-xs font-mono">
            CVSS: {data.cvssScore}
          </span>
        )}
      </div>

      {/* Output Handle */}
      <Handle type="source" position={Position.Right} style={{ background: '#ef4444' }} />
    </div>
  );
};

// Export all node types for React Flow
export const nodeTypes = {
  phase: PhaseNode,
  technique: TechniqueNode,
  indicator: IndicatorNode,
  tool: ToolNode,
  asset: AssetNode,
  vulnerability: VulnerabilityNode,
  threatSource: ThreatSourceNode,
};

// Export individual components for direct use
export default {
  PhaseNode,
  TechniqueNode,
  IndicatorNode,
  ToolNode,
  AssetNode,
  VulnerabilityNode,
  ThreatSourceNode,
  nodeTypes
};
